import { get, send } from "../api.js";

/**
 * The booking system's shapes, as the browser receives them.
 *
 * Written from the checked-in contract (`openapi/wmf-api.json` in WMF-SIte)
 * rather than invented here, and deliberately kept as **data to render** rather
 * than data to reason about. Whether a lesson can be cancelled, what a
 * cancellation would do to a credit and whether a slot is bookable are all
 * decided by FastAPI and arrive already decided; nothing in the front end
 * recomputes any of it. A second opinion here would be a second implementation
 * of a notice period, and the two would disagree on exactly the day it mattered.
 *
 * So the rule for everything below: **render `allowed`, render `reasons`, and
 * never infer.** A disabled button is disabled because the server said so.
 */

/** One reason an action is or is not allowed. `code` is stable; prose is not. */
export interface PolicyReason {
  code: string;
  message: string;
  meta?: Record<string, unknown> | null;
}

export interface ActionDecision {
  allowed: boolean;
  reasons: PolicyReason[];
}

export interface PaymentSummary {
  state: string;
  method: string;
  amount_total: string;
  amount_paid: string;
  amount_due: string;
  currency: string;
}

export interface CreditEffect {
  effect: "none" | "consume" | "restore" | "forfeit" | "unchanged" | "grant";
  credits: number;
  lesson_block_id: string | null;
}

export interface StudentActions {
  cancel: ActionDecision;
  reschedule: ActionDecision;
  book_with_credit: ActionDecision;
  request_onsite: ActionDecision;
}

export interface RecurrenceContext {
  recurrence_plan_id: string | null;
  occurrence_number: number | null;
  student_change_scope: "single_occurrence" | "not_recurring";
}

export interface AppointmentEnrichment {
  appointment_id: number;
  booking_id: number;
  booking_status: string;
  attendance_outcome: string;
  payment: PaymentSummary;
  credit: CreditEffect;
  recurrence: RecurrenceContext;
  actions: StudentActions;
  evaluated_at: string;
}

/** Local mirror fields that do not depend on FastAPI being available. */
export interface StudentLessonScheduleRow {
  bookingId: string;
  ameliaAppointmentId: number | null;
  startsAt: string;
  endsAt: string;
  serviceName: string | null;
  tutorName: string | null;
  providerId: number | null;
  serviceId: number | null;
  status: string;
}

export interface StudentLessonSchedule {
  student: { id: string; fullName: string };
  upcoming: StudentLessonScheduleRow[];
  past: StudentLessonScheduleRow[];
}

export type StudentLessonDirection = "upcoming" | "past";

/** A genuinely paginated Hub-mirror read for the family Lessons screen. */
export interface StudentLessonListRow extends StudentLessonScheduleRow {
  domain: "private" | "school";
  /** False for School/native rows or any row that fails the private mutation boundary. */
  canManageBooking: boolean;
}

export interface StudentLessonPage {
  student: { id: string; fullName: string };
  lessons: StudentLessonListRow[];
  direction: StudentLessonDirection;
  limit: number;
  offset: number;
  total: number;
}

export interface StudentLessonEnrichment {
  state: "ok" | "unavailable";
  code?: string;
  message?: string;
  appointments: AppointmentEnrichment[];
  unavailableBookingIds: number[];
}

/** A lesson from the Hub's mirror, with FastAPI's view of it where there is one. */
export interface StudentLesson extends StudentLessonScheduleRow {
  enrichment: AppointmentEnrichment | null;
  /**
   * FastAPI named this booking specifically as one it cannot vouch for.
   *
   * Distinct from `enrichment === null` during a page-wide outage: this one row
   * is unactionable and the rest of the page is fine. The reason is deliberately
   * not given — on that side it is opaque so it cannot be used to probe another
   * family's bookings, and repeating a vague code to a parent helps nobody.
   */
  unavailable: boolean;
  /** Present on the paginated list; omitted by the private-only Home read. */
  domain?: "private" | "school";
  /** Present on the paginated list; omitted by the private-only Home read. */
  canManageBooking?: boolean;
}

/**
 * A tutor-and-lesson pairing this family may book, as FastAPI decides it.
 *
 * The union of what the Hub can prove the student has been taught and what
 * their lesson blocks entitle them to, with eligibility already worked out.
 * `book_with_credit` and `request_onsite` are rendered, never recomputed —
 * a pairing can be offered for one and refused for the other.
 */
export interface BookingOffering {
  provider_id: number;
  service_id: number;
  /** True only when this currently valid offering matches the remembered booking default. */
  default_preferred?: boolean;
  /** Hub identity added by the Hub catalogue adapter for Hub-native features. */
  tutor_id?: string | null;
  tutor_name?: string | null;
  service_name?: string | null;
  sources: ("hub_relationship" | "lesson_credit")[];
  available_credits: number;
  lesson_block_ids: string[];
  book_with_credit: ActionDecision;
  request_onsite: ActionDecision;
}

export interface BookingOfferings {
  portal_student_id: string;
  evaluated_at: string;
  offerings: BookingOffering[];
}

export interface StudentLessons {
  student: { id: string; fullName: string };
  upcoming: StudentLesson[];
  past: StudentLesson[];
  /**
   * Whether FastAPI has answered yet.
   *
   * `pending` is the deliberate fast-path state: local lesson times are already
   * useful while policy/payment/actions continue loading independently.
   */
  enrichment: { state: "pending" | "ok" | "unavailable"; code?: string; message?: string };
}

export interface LessonCreditBlock {
  tutor_name?: string | null;
  family_shared?: boolean;
  lesson_block_id: string;
  provider_id: number;
  service_id: number;
  service_name: string;
  status: string;
  available_credits: number;
  /** Optional while older service versions remain in circulation. */
  credits_purchased?: number;
  consumed_credits?: number;
  remaining_credits?: number;
  ledger_balance?: number | null;
  booking_held_credits?: number | null;
  upcoming_allocated_credits?: number | null;
  completed_used_credits?: number | null;
  paid?: boolean;
  payment?: PaymentSummary;
  purchased_at?: string;
  expires_at: string;
  source?: "amelia" | "staff" | "legacy_migration" | "goodwill" | "transfer" | "paid_cancellation";
  booking: ActionDecision;
  /** Missing while an older FastAPI version is still serving the Hub. */
  manual_use?: ActionDecision;
}

export interface LessonCreditSummary {
  portal_student_id: string;
  evaluated_at: string;
  total_available_credits: number;
  blocks: LessonCreditBlock[];
}

export interface LessonCreditActivityEntry {
  transaction_id: string;
  transaction_type: "grant" | "use" | "restore" | "adjustment" | "reversal";
  credit_delta: number;
  amelia_appointment_id?: number | null;
  amelia_booking_id?: number | null;
  created_at: string;
}

export interface LessonCreditActivity {
  portal_student_id: string;
  lesson_block_id: string;
  entries: LessonCreditActivityEntry[];
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface AvailabilitySlot {
  starts_at: string;
  ends_at: string;
  provider_id: number;
  service_id: number;
  eligible_credit_count: number;
  booking: ActionDecision;
}

export interface StudentAvailability {
  service_id: number;
  provider_id: number;
  start_date: string;
  end_date: string;
  evaluated_at: string;
  slots: AvailabilitySlot[];
}

export interface CancellationPreview {
  booking_id: number;
  evaluated_at: string;
  starts_at: string;
  notice_seconds: number;
  notice_required_seconds: number;
  notice_satisfied: boolean;
  cancellation: ActionDecision;
  credit: CreditEffect;
  payment_effect: "none" | "amount_remains_due" | "waive_pending" | "manual_refund_review" | "retain_as_rebooking_credit" | "retain_as_cancellation_charge" | "requires_review";
  resulting_outcome: string;
}

export interface ReschedulePreview {
  booking_id: number;
  evaluated_at: string;
  current_starts_at: string;
  requested_starts_at: string;
  provider_id: number;
  service_id: number;
  reschedule: ActionDecision;
  credit: CreditEffect;
  payment_effect: string;
}

export interface MutationResult {
  idempotency_key: string;
  replayed: boolean;
  appointment_id: number;
  booking_id: number;
  booking_status: string;
  attendance_outcome: string;
  /** Absent on older servers; callers must use cautious fallback copy. */
  payment_effect?: "none" | "amount_remains_due" | "waive_pending" | "manual_refund_review" | "retain_as_rebooking_credit" | "retain_as_cancellation_charge" | "requires_review" | null;
  credit: CreditEffect;
  payment: PaymentSummary;
  mirror_sync: { state: "refreshed" | "queued" | "pending_retry" };
  credit_sync?: "synced" | "pending_reconciliation" | "not_applicable";
  evaluated_at: string;
}

/** A block of lessons that was bought, and what is left of it. */
export interface PurchaseHistoryEntry {
  lesson_block_id: string;
  purchased_at: string;
  source: "amelia" | "staff" | "legacy_migration" | "goodwill" | "transfer";
  channel: "amelia_online" | "on_site" | "staff_portal";
  payment_method: string;
  /** Money, kept as the string it arrived as. */
  payment_amount: string;
  payment_currency: string;
  provider_id: number;
  service_id: number;
  service_name: string;
  credits_purchased: number;
  available_credits: number;
  amelia_appointment_id?: number | null;
  amelia_booking_id?: number | null;
}

export interface BookingAuditEvent {
  sequence_number: number;
  event_type: string;
  actor_kind: string;
  channel: string;
  appointment_id?: number | null;
  booking_id?: number | null;
  payment_method?: string | null;
  payment_state?: string | null;
  occurred_at: string;
}

/**
 * One thing that was asked of the booking system, and how it went.
 *
 * A booking, a cancellation, a move — including the ones that failed. This is
 * FastAPI's own audit trail rather than the Hub's mirror, which is why it can
 * answer "did that cancellation actually go through?" when the mirror shows
 * nothing either way.
 */
export interface OperationHistoryEntry {
  operation_id: string;
  operation_type: string;
  status: string;
  actor_kind: string;
  channel: string;
  provider_id: number;
  service_id: number;
  requested_start: string;
  lesson_block_id: string | null;
  appointment_id?: number | null;
  booking_id?: number | null;
  payment_method?: string | null;
  payment_state?: string | null;
  created_at: string;
  completed_at: string | null;
  events: BookingAuditEvent[];
}

export interface BookingHistory {
  portal_student_id: string;
  evaluated_at: string;
  purchases: PurchaseHistoryEntry[];
  operations: OperationHistoryEntry[];
  limit: number;
  offset: number;
  purchase_total?: number;
  operation_total?: number;
}

export interface BookingHistoryPage extends BookingHistory {
  purchase_total: number;
  operation_total: number;
}

export interface BookingHistoryPageParams {
  purchaseLimit: number;
  purchaseOffset: number;
  operationLimit: number;
  operationOffset: number;
}

/**
 * A key for one logical action, made once and reused on every retry.
 *
 * This is the whole mechanism. Made fresh per attempt it would protect nothing —
 * the point is that pressing "book" twice, or pressing it once over a connection
 * that dropped, sends the *same* key, and FastAPI replies with the original
 * result instead of booking a second lesson and spending a second credit.
 *
 * So a component holds one of these for as long as the user is trying to do the
 * thing, and gets a new one only when they start over.
 */
export function idempotencyKey(): string {
  return crypto.randomUUID();
}

const validTotal = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

/**
 * A backend that predates per-collection totals ignores the paging parameters
 * and returns the whole history. With both totals absent and both lists present
 * that list is complete, so it is paged here. Totals that are present but
 * invalid, only one total, or lists that are not arrays are not a shape this
 * page can trust, and are refused instead of shown as empty.
 */
export function normaliseHistoryPage(
  history: BookingHistory,
  params: BookingHistoryPageParams,
): BookingHistoryPage {
  const body = history as Partial<BookingHistory> | null;
  const unavailable = () => new Error("Payment and request history is unavailable.");
  if (typeof body !== "object" || body === null) throw unavailable();
  if (!Array.isArray(body.purchases) || !Array.isArray(body.operations)) throw unavailable();
  if (body.purchase_total === undefined && body.operation_total === undefined) {
    const purchaseTotal = body.purchases.length;
    const operationTotal = body.operations.length;
    return {
      ...(body as BookingHistory),
      purchases: body.purchases.slice(params.purchaseOffset, params.purchaseOffset + params.purchaseLimit),
      operations: body.operations.slice(params.operationOffset, params.operationOffset + params.operationLimit),
      purchase_total: purchaseTotal,
      operation_total: operationTotal,
    };
  }
  if (!validTotal(body.purchase_total) || !validTotal(body.operation_total)) throw unavailable();
  return history as BookingHistoryPage;
}

const base = (studentId: string) => `/student-booking/students/${encodeURIComponent(studentId)}`;

export const booking = {
  // Legacy combined read retained for any existing direct callers/tests. Home
  // uses the split reads below so lesson times are never held behind FastAPI.
  lessons: (studentId: string) => get<StudentLessons>(`${base(studentId)}/lessons`),

  lessonSchedule: (studentId: string) =>
    get<StudentLessonSchedule>(`${base(studentId)}/lesson-schedule`),

  lessonList: (
    studentId: string,
    direction: StudentLessonDirection,
    limit = 15,
    offset = 0,
  ) =>
    get<StudentLessonPage>(`${base(studentId)}/lesson-list`, {
      direction,
      limit: String(limit),
      offset: String(offset),
    }),

  lessonEnrichment: (studentId: string, bookingIds: number[]) =>
    send<StudentLessonEnrichment>("POST", `${base(studentId)}/lesson-enrichment`, { bookingIds }),

  offerings: (studentId: string) => get<BookingOfferings>(`${base(studentId)}/offerings`),

  history: (studentId: string) => get<BookingHistory>(`${base(studentId)}/history`),

  historyPage: async (studentId: string, params: BookingHistoryPageParams) => {
    const history = await get<BookingHistory>(`${base(studentId)}/history`, {
      purchase_limit: String(params.purchaseLimit),
      purchase_offset: String(params.purchaseOffset),
      operation_limit: String(params.operationLimit),
      operation_offset: String(params.operationOffset),
    });
    return normaliseHistoryPage(history, params);
  },

  credits: (studentId: string, params?: { includeUsage?: boolean }) =>
    get<LessonCreditSummary>(
      `${base(studentId)}/lesson-credits`,
      params?.includeUsage ? { include_usage: "true" } : undefined,
    ),

  creditActivity: (studentId: string, blockId: string, params: { limit: number; offset: number }) =>
    get<LessonCreditActivity>(`${base(studentId)}/lesson-credits/${encodeURIComponent(blockId)}/activity`, {
      limit: String(params.limit),
      offset: String(params.offset),
    }),

  availability: (
    studentId: string,
    params: {
      service_id: string;
      provider_id: string;
      start_date: string;
      end_date: string;
      lesson_block_id?: string;
    },
  ) => get<StudentAvailability>(`${base(studentId)}/availability`, params),

  cancellationPreview: (studentId: string, bookingId: string, reason: string) =>
    send<CancellationPreview>(
      "POST",
      `${base(studentId)}/appointments/${encodeURIComponent(bookingId)}/cancellation-preview`,
      { reason },
    ),

  cancel: (studentId: string, bookingId: string, reason: string, key: string) =>
    send<MutationResult>(
      "POST",
      `${base(studentId)}/appointments/${encodeURIComponent(bookingId)}/cancel`,
      { reason },
      { "Idempotency-Key": key },
    ),

  reschedulePreview: (studentId: string, bookingId: string, startsAt: string) =>
    send<ReschedulePreview>(
      "POST",
      `${base(studentId)}/appointments/${encodeURIComponent(bookingId)}/rescheduling-preview`,
      { starts_at: startsAt },
    ),

  reschedule: (studentId: string, bookingId: string, startsAt: string, key: string) =>
    send<MutationResult>(
      "POST",
      `${base(studentId)}/appointments/${encodeURIComponent(bookingId)}/reschedule`,
      { starts_at: startsAt },
      { "Idempotency-Key": key },
    ),

  bookWithCredit: (
    studentId: string,
    body: {
      provider_id: number;
      service_id: number;
      starts_at: string;
      lesson_block_id?: string | null;
      notes?: string;
    },
    key: string,
  ) =>
    send<MutationResult>("POST", `${base(studentId)}/appointments/credit-funded`, body, {
      "Idempotency-Key": key,
    }),

  requestOnSite: (
    studentId: string,
    body: {
      provider_id: number;
      service_id: number;
      starts_at: string;
      payment_method: "cash" | "card_terminal" | "bank_transfer";
      notes?: string;
    },
    key: string,
  ) =>
    send<{ id: string; status: string; starts_at: string; payment: PaymentSummary }>(
      "POST",
      `${base(studentId)}/lesson-requests`,
      body,
      { "Idempotency-Key": key },
    ),
};


export interface CalendarFeedState {
  enabled: boolean;
  active: boolean;
  createdAt: string | null;
}

export interface CalendarFeedLink {
  active: true;
  path: string;
  createdAt: string;
}

const calendarFeedPath = (studentId: string) =>
  `/me/students/${encodeURIComponent(studentId)}/calendar-feed`;

export const calendarFeeds = {
  state: (studentId: string) =>
    get<CalendarFeedState>(calendarFeedPath(studentId)),

  rotate: (studentId: string) =>
    send<CalendarFeedLink>("POST", calendarFeedPath(studentId)),

  revoke: (studentId: string) =>
    send<{ active: false }>("DELETE", calendarFeedPath(studentId)),
};
